import React from 'react';

import { IFormHandleExtended } from '../../../../components/FormQuiz';
import { ParticipantStep } from '../../../../../../../types/v3Types';
import { FormError } from '@wix/form-viewer/widget';
import type { ChatStepContext } from '../../../../../../../contexts/storage-contexts/AIChat/interfaces';
import type { AiTutorUserEntry } from '../../../../../../../contexts/main/biInterfaces';

export type AiChatMessageSource = 'typed' | 'preset';

export interface PendingAiChatMessage {
  id: string;
  message: string;
  source: AiChatMessageSource;
  // The step the message was asked from, captured when it was queued
  stepContext?: ChatStepContext;
  // Participant step id of that step, for BI
  stepId?: string;
}

export interface ISidebarLayoutBaseContext {
  formQuizRef: React.RefObject<IFormHandleExtended>; // we need it to have a way to submit a form from btn located in a sibling component
  formValidity: FormError[];
  setFormValidity: (formValidity: FormError[]) => void;
  currentStep: ParticipantStep;
  isCurrentStepHidden: boolean;
  // The step the learner is looking at; undefined on the overview, a hidden
  // step or the mobile list screen
  chatStepContext?: ChatStepContext;
  // Participant step id of that same step, for BI
  stepOnScreenId?: string;
  goToCurrentStep(): void;
  pageRef: React.RefObject<HTMLElement>;
  videoStepAccepted: boolean;
  setVideoStepAccepted(videoStepAccepted: boolean): void;
  isVideoLoadingError: boolean;
  setIsVideoLoadingError(isError: boolean): void;
  resolveCurrentStep(): Promise<void>;
  isAutoPlayVideo: boolean;
  changeAutoplayOptions(isAutoplayEnabled: boolean): Promise<void>;
  checkAutoNavigate(): Promise<void>;
  isSidebarCollapsed: boolean;
  isCollapsibleSidebarEnabled: boolean;
  toggleSidebarCollapse: () => void;
  isAiChatVisible: boolean;
  isAiChatEnabled: boolean;
  // userEntry is reported only when the toggle opens the chat
  toggleAiChatVisibility: (userEntry?: AiTutorUserEntry) => void;
  pendingAiChatMessage: PendingAiChatMessage | null;
  openAiChatWithMessage: (message: string, source: AiChatMessageSource) => void;
  clearPendingAiChatMessage: (id: string) => void;
}

export const SidebarLayoutBaseContext =
  React.createContext<ISidebarLayoutBaseContext>({
    formQuizRef: null,
    formValidity: null,
    setFormValidity: () => {},
    currentStep: null,
    isCurrentStepHidden: false,
    goToCurrentStep: () => {},
    pageRef: null,
    videoStepAccepted: false,
    setVideoStepAccepted: () => {},
    isVideoLoadingError: false,
    setIsVideoLoadingError: () => {},
    resolveCurrentStep: async () => {},
    isAutoPlayVideo: false,
    changeAutoplayOptions: async () => {},
    checkAutoNavigate: async () => {},
    isSidebarCollapsed: false,
    isCollapsibleSidebarEnabled: false,
    toggleSidebarCollapse: () => {},
    isAiChatVisible: false,
    isAiChatEnabled: false,
    toggleAiChatVisibility: () => {},
    pendingAiChatMessage: null,
    openAiChatWithMessage: () => {},
    clearPendingAiChatMessage: () => {},
  });

export function useSidebarLayoutBase(): ISidebarLayoutBaseContext {
  return React.useContext(SidebarLayoutBaseContext);
}
